import { useEffect, useRef, useState, type FormEvent } from "react";
import { AlertTriangle, Building2, Pencil, Save, Trash2, X } from "lucide-react";
import { api, ApiError } from "../../api";
import { Button, Card, Field, cx } from "../../ui";

type BuildingRow = {
  id: string;
  name: string;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string;
  postalCode: string;
  timezone: string;
  createdAt: string;
  updatedAt: string;
  canDelete: boolean;
};

type EditState = {
  id: string;
  name: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  timezone: string;
};

const TIMEZONES = [
  "America/Chicago",
  "America/New_York",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Anchorage",
  "Pacific/Honolulu",
];

export function AdminBuildings() {
  const [rows, setRows] = useState<BuildingRow[] | null>(null);
  const [edit, setEdit] = useState<EditState | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [editBusy, setEditBusy] = useState(false);

  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [confirmName, setConfirmName] = useState("");
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const editPanelRef = useRef<HTMLDivElement>(null);

  const reload = async () => {
    const r = await api.get<{ data: BuildingRow[] }>("/api/admin/buildings");
    setRows(r.data);
  };

  useEffect(() => {
    void reload();
  }, []);

  const startEdit = (b: BuildingRow) => {
    setEdit({
      id: b.id,
      name: b.name,
      addressLine1: b.addressLine1,
      addressLine2: b.addressLine2 ?? "",
      city: b.city,
      state: b.state,
      postalCode: b.postalCode,
      timezone: b.timezone,
    });
    setEditError(null);
    setConfirmingId(null);
    requestAnimationFrame(() => {
      editPanelRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  const saveEdit = async (e: FormEvent) => {
    e.preventDefault();
    if (!edit) return;
    setEditBusy(true);
    setEditError(null);
    try {
      await api.patch(`/api/admin/buildings/${edit.id}`, {
        name: edit.name,
        addressLine1: edit.addressLine1,
        addressLine2: edit.addressLine2 || null,
        city: edit.city,
        state: edit.state.toUpperCase(),
        postalCode: edit.postalCode,
        timezone: edit.timezone,
      });
      setEdit(null);
      await reload();
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string; issues?: unknown };
        setEditError(
          body.issues
            ? "Some fields are invalid."
            : `Save failed: ${body.error ?? err.status}`,
        );
      } else setEditError(String(err));
    } finally {
      setEditBusy(false);
    }
  };

  const startDelete = (b: BuildingRow) => {
    setConfirmingId(b.id);
    setConfirmName("");
    setDeleteError(null);
    setEdit(null);
  };

  const confirmDelete = async (b: BuildingRow) => {
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      // The api.del() helper doesn't carry a body, but the DELETE
      // endpoint requires `confirmName` in the body. Use fetch directly.
      const res = await fetch(`/api/admin/buildings/${b.id}`, {
        method: "DELETE",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ confirmName }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
          message?: string;
        };
        if (body.error === "confirmation_mismatch") {
          throw new Error(
            "The name you typed doesn't match the building name.",
          );
        }
        if (body.error === "would_remove_last_admin_role") {
          throw new Error(
            body.message ??
              "Deleting this building would remove your only admin role.",
          );
        }
        throw new Error(body.message ?? body.error ?? `Delete failed (${res.status})`);
      }
      setConfirmingId(null);
      setConfirmName("");
      await reload();
    } catch (err) {
      setDeleteError(String(err instanceof Error ? err.message : err));
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div>
      <Card className="!p-0 overflow-hidden">
        <div className="flex items-center justify-between p-5 sm:p-6 border-b border-ink-100">
          <div>
            <h2 className="text-ink-800 m-0 inline-flex items-center gap-2">
              <Building2 size={20} /> Buildings
            </h2>
            <p className="text-xs text-ink-500 mt-0.5">
              Buildings you manage. Property Managers and Admins can edit
              information; only Admins can permanently delete a building.
            </p>
          </div>
        </div>

        {rows === null ? (
          <p className="p-6 text-ink-500 text-sm">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="p-6 text-ink-500 text-sm">
            You don't manage any buildings yet.
          </p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Address</th>
                  <th>Timezone</th>
                  <th>Created</th>
                  <th className="w-1"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((b) => (
                  <tr
                    key={b.id}
                    className={cx(
                      edit?.id === b.id && "bg-brand-50",
                      confirmingId === b.id && "bg-status-rejected-bg/40",
                    )}
                  >
                    <td className="font-medium text-ink-800">{b.name}</td>
                    <td className="text-ink-600">
                      {b.addressLine1}
                      {b.addressLine2 && <>, {b.addressLine2}</>}
                      <div className="text-xs text-ink-500">
                        {b.city}, {b.state} {b.postalCode}
                      </div>
                    </td>
                    <td>
                      <code>{b.timezone}</code>
                    </td>
                    <td className="text-xs text-ink-500">
                      {new Date(b.createdAt).toLocaleDateString()}
                    </td>
                    <td>
                      <div className="flex gap-1.5 justify-end">
                        <Button
                          size="sm"
                          variant={edit?.id === b.id ? "primary" : "secondary"}
                          onClick={() => startEdit(b)}
                        >
                          <Pencil size={14} /> Edit
                        </Button>
                        {b.canDelete && (
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => startDelete(b)}
                          >
                            <Trash2 size={14} /> Delete
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {edit && (
        <div ref={editPanelRef} className="scroll-mt-20">
          <Card>
            <div className="flex items-start justify-between gap-3 mb-4">
              <h3 className="text-ink-800 m-0">
                Edit{" "}
                <span className="text-brand-700">
                  {rows?.find((r) => r.id === edit.id)?.name ?? "building"}
                </span>
              </h3>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEdit(null)}
                aria-label="Cancel edit"
              >
                <X size={14} /> Cancel
              </Button>
            </div>
            <form onSubmit={saveEdit}>
              <Field label="Name">
                <input
                  className="input"
                  value={edit.name}
                  onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                  required
                />
              </Field>
              <Field label="Address line 1">
                <input
                  className="input"
                  value={edit.addressLine1}
                  onChange={(e) =>
                    setEdit({ ...edit, addressLine1: e.target.value })
                  }
                  required
                />
              </Field>
              <Field label="Address line 2 (optional)">
                <input
                  className="input"
                  value={edit.addressLine2}
                  onChange={(e) =>
                    setEdit({ ...edit, addressLine2: e.target.value })
                  }
                />
              </Field>
              <div className="field-row">
                <Field label="City" className="flex-[2]">
                  <input
                    className="input"
                    value={edit.city}
                    onChange={(e) => setEdit({ ...edit, city: e.target.value })}
                    required
                  />
                </Field>
                <Field label="State">
                  <input
                    className="input uppercase"
                    value={edit.state}
                    onChange={(e) =>
                      setEdit({
                        ...edit,
                        state: e.target.value.slice(0, 2).toUpperCase(),
                      })
                    }
                    maxLength={2}
                    pattern="[A-Z]{2}"
                    required
                  />
                </Field>
                <Field label="Postal code">
                  <input
                    className="input"
                    value={edit.postalCode}
                    onChange={(e) =>
                      setEdit({ ...edit, postalCode: e.target.value })
                    }
                    required
                  />
                </Field>
              </div>
              <Field label="Timezone">
                <select
                  className="select"
                  value={edit.timezone}
                  onChange={(e) =>
                    setEdit({ ...edit, timezone: e.target.value })
                  }
                >
                  {[edit.timezone, ...TIMEZONES.filter((t) => t !== edit.timezone)].map(
                    (tz) => (
                      <option key={tz} value={tz}>
                        {tz}
                      </option>
                    ),
                  )}
                </select>
              </Field>
              {editError && <div className="alert-error">{editError}</div>}
              <div className="flex gap-2">
                <Button
                  type="submit"
                  variant="primary"
                  loading={editBusy}
                >
                  <Save size={14} />
                  Save changes
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setEdit(null)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {confirmingId &&
        (() => {
          const b = rows?.find((r) => r.id === confirmingId);
          if (!b) return null;
          return (
            <Card className="border-2 border-status-rejected-fg/30 bg-status-rejected-bg/30">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 inline-flex items-center justify-center w-8 h-8 rounded-full bg-status-rejected-fg/15 text-status-rejected-fg">
                  <AlertTriangle size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-status-rejected-fg m-0">
                    Permanently delete this building?
                  </h3>
                  <p className="text-sm text-ink-700 mt-1 mb-3">
                    This cascades through every project, document, fee,
                    inspection, and user role attached to{" "}
                    <strong>{b.name}</strong>. The audit trail is preserved
                    with the building pointer cleared. This cannot be undone.
                  </p>
                  <Field
                    label={
                      <>
                        Type the building name to confirm:{" "}
                        <code className="normal-case">{b.name}</code>
                      </>
                    }
                  >
                    <input
                      className="input"
                      value={confirmName}
                      onChange={(e) => setConfirmName(e.target.value)}
                      autoFocus
                      placeholder={b.name}
                    />
                  </Field>
                  {deleteError && (
                    <div className="alert-error">{deleteError}</div>
                  )}
                  <div className="flex gap-2 mt-2">
                    <Button
                      variant="danger"
                      disabled={confirmName !== b.name || deleteBusy}
                      loading={deleteBusy}
                      onClick={() => void confirmDelete(b)}
                    >
                      <Trash2 size={14} />
                      Yes, permanently delete
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setConfirmingId(null);
                        setConfirmName("");
                        setDeleteError(null);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          );
        })()}
    </div>
  );
}
